import { useQuery } from "convex/react";
import {
  CheckCircle,
  FileText,
  Inbox,
  Loader2,
  PartyPopper,
} from "lucide-react";
import { useParams } from "react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Navbar,
  PageContainer,
  Progress,
  StatutEvaluationBadge,
} from "@repo/ui";
import { useCandidateAuthStore, useCandidateLogout } from "@repo/auth";
import { buildParcoursDescription, getEvaluationTitle } from "@repo/utils";
import { StatutGlobalEnum, TypeEvalEnum, estEvaluationSoumise } from "@convex/schema";

export function ParcoursView() {
  const { id } = useParams<{ id: string }>();
  const token = useCandidateAuthStore((s) => s.token);
  const firstName = useCandidateAuthStore((s) => s.firstName);
  const email = useCandidateAuthStore((s) => s.email);
  const { logout } = useCandidateLogout();

  const result = useQuery(
    api.parcoursEvaluations.obtenirParcoursComplet,
    id && token
      ? { parcoursId: id as Id<"parcoursEvaluations">, identifiant: token }
      : "skip",
  );

  if (result === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if ("erreur" in result) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">Erreur</h1>
          <p className="mt-2 text-muted-foreground">{result.erreur}</p>
        </div>
      </div>
    );
  }

  const { parcours, evaluations, progression } = result;

  const handleEvaluationClick = (
    evaluationId: Id<"evaluations">,
    typeEval: string,
  ) => {
    const tokenParam = process.env.NODE_ENV === "development" && token ? `?token=${encodeURIComponent(token)}` : "";
    if (typeEval === TypeEvalEnum.FORM) {
      const url = `${process.env.BUN_PUBLIC_APP_FORM_URL}/e/${evaluationId}${tokenParam}`;
      window.open(url, "_blank");
    } else if (typeEval === TypeEvalEnum.INBASKET) {
      const url = `${process.env.BUN_PUBLIC_APP_INBASKET_URL}/e/${evaluationId}${tokenParam}`;
      window.open(url, "_blank");
    } else if (typeEval === TypeEvalEnum.ENT_STRUCT) {
      const url = `${process.env.BUN_PUBLIC_APP_ENT_STRUCT_URL}/e/${evaluationId}${tokenParam}`;
      window.open(url, "_blank");
    }
  };

  const getEvaluationIcon = (typeEval: string) => {
    switch (typeEval) {
      case TypeEvalEnum.FORM:
        return <FileText className="h-5 w-5 text-muted-foreground" />;
      case TypeEvalEnum.INBASKET:
        return <Inbox className="h-5 w-5 text-muted-foreground" />;
      case TypeEvalEnum.ENT_STRUCT:
        return <FileText className="h-5 w-5 text-muted-foreground" />;
      default:
        return <FileText className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getTitleForEvaluation = (evaluation: (typeof evaluations)[0]) =>
    getEvaluationTitle(
      {
        typeEval: evaluation.typeEval,
        template:
          evaluation.typeEval === TypeEvalEnum.FORM ||
          evaluation.typeEval === TypeEvalEnum.ENT_STRUCT
            ? evaluation.template
            : undefined,
      },
      "candidat",
    );

  const getEvaluationDescription = (evaluation: (typeof evaluations)[0]) => {
    if (evaluation.typeEval === TypeEvalEnum.FORM) {
      const template = evaluation.template ?? null;
      switch (template) {
        case "autodiag":
          return "Questionnaire d'auto-évaluation de vos compétences";
        case "egogramme":
          return "Questionnaire sur votre style relationnel";
        case "pc-fit":
          return "Classement de vos priorités culturelles";
        case "copsoq3":
          return "Questionnaire sur vos conditions de travail";
        default:
          return "Questionnaire à compléter";
      }
    }
    if (evaluation.typeEval === TypeEvalEnum.ENT_STRUCT) {
      return "Entretien avec un évaluateur";
    }
    return "Gestion de boîte de réception simulée";
  };

  // Calcul des initiales pour l'avatar
  const estAnonyme = result && "parcours" in result && result.parcours.anonyme === true;

  const initials = estAnonyme
    ? token?.charAt(0).toUpperCase() || "?"
    : firstName && token
      ? `${firstName.charAt(0)}${token.charAt(0)}`.toUpperCase()
      : token?.charAt(0).toUpperCase() || "?";

  const displayName = estAnonyme
    ? token || ""
    : `${firstName} ${token}`.trim();

  return (
    <div className="min-h-screen bg-background">
      <Navbar
        title="Mon Parcours"
        userInfo={{
          displayName,
          subtitle: estAnonyme ? "" : (email || ""),
          initials,
        }}
        onLogout={logout}
      />

<main>
      <PageContainer>
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900">{parcours.titre}</h2>
          <p className="mt-2 text-gray-600">
            {buildParcoursDescription(
              evaluations.map((e) => ({
                typeEval: e.typeEval,
                template:
                  e.typeEval === TypeEvalEnum.FORM ||
                  e.typeEval === TypeEvalEnum.ENT_STRUCT
                    ? e.template
                    : undefined,
              })),
              "candidat",
            )}
          </p>
        </div>

        {/* Congratulations Banner - Show when all evaluations are completed */}
        {progression.pourcentage === 100 && (
          <Card className="mb-8 border-green-200 bg-linear-to-r from-green-50 to-emerald-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="shrink-0">
                  <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
                    <PartyPopper className="h-8 w-8 text-green-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-green-800">
                    🎉 Bravo, vous avez terminé votre parcours d'évaluation !
                  </h3>
                  <p className="mt-1 text-green-700">
                    Vous avez complété toutes les évaluations de ce parcours.
                    Votre évaluateur a été notifié et reviendra vers vous
                    prochainement.
                  </p>
                </div>
                <div className="hidden md:flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-6 w-6" />
                  <span className="font-semibold">100%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Evaluations List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Vos évaluations</CardTitle>
                <CardDescription>
                  Cliquez sur une évaluation pour y accéder
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 min-w-35">
                <Progress
                  value={progression.pourcentage}
                  className={`h-2 flex-1 ${progression.pourcentage === 100 ? "[&>div]:bg-green-500" : ""}`}
                />
                <span
                  className={`text-sm font-medium whitespace-nowrap ${progression.pourcentage === 100 ? "text-green-600" : ""}`}
                >
                  {progression.pourcentage}%
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {evaluations.map((evaluation) => (
                <div
                  key={evaluation._id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border border-charte-line rounded-surface transition-colors duration-150 hover:border-charte-line-accent"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-surface-3 rounded-controle shrink-0">
                      {getEvaluationIcon(evaluation.typeEval)}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {getTitleForEvaluation(evaluation)}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {getEvaluationDescription(evaluation)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 sm:shrink-0">
                    <StatutEvaluationBadge statut={evaluation.statut} />
                    <Button
                      className="flex-1 sm:flex-none"
                      onClick={() =>
                        handleEvaluationClick(
                          evaluation._id,
                          evaluation.typeEval,
                        )
                      }
                      variant={
                        estEvaluationSoumise(evaluation.statut)
                          ? "outline"
                          : "default"
                      }
                    >
                      {estEvaluationSoumise(evaluation.statut)
                        ? "Voir"
                        : evaluation.statut === StatutGlobalEnum.EN_COURS
                          ? "Continuer"
                          : "Commencer"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </PageContainer>
      </main>
    </div>
  );
}
